import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, API } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { FloppyDisk, FilePdf, CheckCircle, Plus, Trash, PencilSimple, MagnifyingGlass, X } from "@phosphor-icons/react";
import { downloadJsonFile } from "../../lib/downloads";

const STATUSES = ["open", "in_progress", "closed"];
const SEVERITIES = ["minor", "major", "critical"];

const DETAIL_FIELDS = {
  ncr: [
    { k: "source", type: "select", options: ["Production", "Supplier", "Internal audit", "Customer", "Other"] },
    { k: "root_cause", type: "area" },
    { k: "correction", type: "area" },
    { k: "corrective_action", type: "area" },
    { k: "disposition", type: "text" },
  ],
  capa: [
    { k: "root_cause", type: "area" },
    { k: "correction", type: "area" },
    { k: "corrective_action", type: "area" },
    { k: "effectiveness_check", type: "area" },
  ],
  complaint: [
    { k: "complainant", type: "text" },
    { k: "device_lot", type: "text" },
    { k: "received_date", type: "date" },
    { k: "investigation", type: "area" },
    { k: "incident_code", type: "text" },
    { k: "reportable", type: "select", options: ["No", "Yes"] },
    {
      k: "vigilance_assessment",
      type: "select",
      options: ["not_assessed", "needs_review", "reportable", "not_reportable"],
      optionPrefix: "qms.vigilance.",
    },
    { k: "vigilance_basis", type: "area" },
    { k: "response", type: "area" },
    { k: "outcome", type: "text" },
  ],
  recall: [
    { k: "scope", type: "text" },
    { k: "lots_affected", type: "text" },
    { k: "units_affected", type: "text" },
    { k: "reason", type: "area" },
    { k: "strategy", type: "area" },
    { k: "communication", type: "area" },
    { k: "effectiveness_check", type: "area" },
  ],
  fsca: [
    { k: "fsca_type", type: "select", options: ["Device recall", "Device modification", "Device exchange", "Software update", "Destruction"] },
    { k: "fsn_ref", type: "text" },
    { k: "competent_authority", type: "text" },
    { k: "countries", type: "text" },
    { k: "actions", type: "area" },
    { k: "timeline", type: "text" },
  ],
};

const EMPTY = {
  title: "", description: "", status: "open", severity: "",
  date_opened: "", date_closed: "", responsible: "", details: {},
};

const STATUS_STYLE = {
  open: "bg-amber-50 text-amber-800 border-amber-200",
  in_progress: "bg-blue-50 text-blue-800 border-blue-200",
  closed: "bg-emerald-50 text-emerald-800 border-emerald-200",
};

export default function QmsTab({ project, type, deviceId }) {
  const t = useT();
  const [records, setRecords] = useState([]);
  const [traceRecords, setTraceRecords] = useState([]);
  const [originRecords, setOriginRecords] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | record.id
  const [draft, setDraft] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [okMsg, setOkMsg] = useState("");
  const [search, setSearch] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fSeverity, setFSeverity] = useState("");
  const [downloading, setDownloading] = useState("");
  const [creatingFsca, setCreatingFsca] = useState(false);

  const load = useCallback(async () => {
    const [r, trace, complaints, ncrs] = await Promise.all([
      api.get(`/projects/${project.id}/qms/${type}`, {
        params: deviceId ? { device_id: deviceId } : {},
      }),
      api.get(`/projects/${project.id}/trace`, {
        params: deviceId ? { device_id: deviceId } : {},
      }).catch(() => ({ data: [] })),
      type === "capa"
        ? api.get(`/projects/${project.id}/qms/complaint`, { params: deviceId ? { device_id: deviceId } : {} }).catch(() => ({ data: [] }))
        : Promise.resolve({ data: [] }),
      type === "capa"
        ? api.get(`/projects/${project.id}/qms/ncr`, { params: deviceId ? { device_id: deviceId } : {} }).catch(() => ({ data: [] }))
        : Promise.resolve({ data: [] }),
    ]);
    setRecords(r.data || []);
    setTraceRecords(trace.data || []);
    setOriginRecords([...(complaints.data || []), ...(ncrs.data || [])]);
  }, [project?.id, type, deviceId]);

  useEffect(() => { if (project?.id) load(); }, [project?.id, load]);

  const stats = useMemo(() => ({
    total: records.length,
    open: records.filter((r) => r.status === "open").length,
    in_progress: records.filter((r) => r.status === "in_progress").length,
    closed: records.filter((r) => r.status === "closed").length,
  }), [records]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return records.filter((r) => {
      if (fStatus && r.status !== fStatus) return false;
      if (fSeverity && r.severity !== fSeverity) return false;
      if (q && !`${r.ref} ${r.title} ${r.description}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [records, search, fStatus, fSeverity]);

  function startNew() {
    setEditing("new");
    setDraft({ ...EMPTY, device_id: deviceId, trace_record_id: "", source_qms_record_id: "" });
    setOkMsg("");
  }

  function startEdit(rec) {
    setEditing(rec.id);
    setDraft({ ...EMPTY, ...rec, details: { ...(rec.details || {}) } });
    setOkMsg("");
  }

  async function save() {
    if (!draft.title.trim()) return;
    setSaving(true);
    setOkMsg("");
    try {
      const payload = {
        device_id: deviceId,
        trace_record_id: draft.trace_record_id || null,
        source_qms_record_id: draft.source_qms_record_id || null,
        title: draft.title, description: draft.description, status: draft.status,
        severity: draft.severity, date_opened: draft.date_opened, date_closed: draft.date_closed,
        responsible: draft.responsible, details: draft.details,
      };
      if (editing === "new") await api.post(`/projects/${project.id}/qms/${type}`, payload);
      else await api.put(`/projects/${project.id}/qms/${type}/${editing}`, payload);
      setOkMsg(t("qms.saved"));
      setEditing(null);
      await load();
    } finally { setSaving(false); }
  }

  async function remove(rec) {
    if (!window.confirm(t("qms.confirm_delete"))) return;
    await api.delete(`/projects/${project.id}/qms/${type}/${rec.id}`);
    if (editing === rec.id) setEditing(null);
    await load();
  }

  async function downloadPdf(rec) {
    setDownloading(rec.id);
    try {
      const lang = localStorage.getItem("rg_lang") || "en";
      const res = await fetch(`${API}/projects/${project.id}/qms/${type}/${rec.id}/pdf?lang=${lang}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("rg_token")}` },
        credentials: "include",
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${rec.ref}_${project.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally { setDownloading(""); }
  }

  function exportJson(rec = null) {
    const exportedAt = new Date().toISOString();
    const base = {
      schema: "airegulatory.qms-source.v1",
      document_type: `${type}_source_data`,
      exported_at: exportedAt,
      project: { id: project.id, name: project.name },
      limitations: [
        "Exportación de los datos guardados; no determina cumplimiento ni sustituye una evaluación humana.",
        ...(type === "complaint" ? ["La aplicación no decide automáticamente si un incidente debe notificarse; revise el caso y las obligaciones aplicables."] : []),
      ],
    };
    if (rec) {
      downloadJsonFile({ ...base, record: rec }, `${rec.ref}_${project.id.slice(0, 8)}.json`);
      return;
    }
    downloadJsonFile({ ...base, records }, `RA-${type.toUpperCase()}_${project.id.slice(0, 8)}.json`);
  }

  async function createFscaDraft() {
    setCreatingFsca(true);
    try {
      const r = await api.post(`/projects/${project.id}/qms/fsca`, {
        device_id: deviceId,
        title: `FSCA — ${draft.ref || ""} ${draft.title}`.trim(),
        description: `Source complaint / Reclamación origen: ${draft.ref || "—"} — ${draft.title}. ${draft.description || ""}`.trim(),
        status: "open",
        severity: draft.severity || "",
        date_opened: new Date().toISOString().slice(0, 10),
        responsible: draft.responsible || "",
        details: { actions: draft.details.investigation || "" },
        trace_record_id: draft.trace_record_id || null,
      });
      setOkMsg(`${t("qms.fsca_created")} ${r.data.ref}`);
    } finally { setCreatingFsca(false); }
  }

  const fields = DETAIL_FIELDS[type] || [];

  return (
    <div className="space-y-5" data-testid={`qms-${type}-tab`}>
      <div className="grid-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-extrabold tracking-tight">{t(`qms.${type}.title`)}</h2>
            <p className="text-sm text-zinc-600 mt-1">{t(`qms.${type}.sub`)}</p>
          </div>
          <button type="button" onClick={() => exportJson()} disabled={!records.length}
            className="rounded-sm border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-xs font-semibold text-[var(--text)] hover:bg-[var(--surface-hover)] disabled:opacity-40"
            data-testid={`qms-${type}-export-json`}>
            {t("exports.download_history_json")}
          </button>
        </div>
      </div>

      {/* Dashboard */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid={`qms-${type}-dashboard`}>
        <Stat label={t("qms.total")} value={stats.total} testid={`qms-${type}-stat-total`} />
        <Stat label={t("qms.open")} value={stats.open} accent="text-amber-700" testid={`qms-${type}-stat-open`} />
        <Stat label={t("qms.in_progress")} value={stats.in_progress} accent="text-blue-700" testid={`qms-${type}-stat-progress`} />
        <Stat label={t("qms.closed")} value={stats.closed} accent="text-emerald-700" testid={`qms-${type}-stat-closed`} />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[180px]">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("qms.search")}
            data-testid={`qms-${type}-search`}
            className="w-full border border-zinc-200 pl-8 pr-2 py-2 rounded-sm text-sm focus:border-blue-600 focus:outline-none" />
        </div>
        <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} data-testid={`qms-${type}-filter-status`}
          className="border border-zinc-200 px-2 py-2 rounded-sm text-xs bg-white focus:border-blue-600 focus:outline-none">
          <option value="">{t("qms.filter_status")}</option>
          {STATUSES.map((s) => <option key={s} value={s}>{t(`qms.st.${s}`)}</option>)}
        </select>
        <select value={fSeverity} onChange={(e) => setFSeverity(e.target.value)} data-testid={`qms-${type}-filter-severity`}
          className="border border-zinc-200 px-2 py-2 rounded-sm text-xs bg-white focus:border-blue-600 focus:outline-none">
          <option value="">{t("qms.filter_severity")}</option>
          {SEVERITIES.map((s) => <option key={s} value={s}>{t(`qms.sev.${s}`)}</option>)}
        </select>
        <button onClick={startNew} data-testid={`qms-${type}-new`} className="btn-primary inline-flex items-center gap-1 px-3 py-2 text-xs">
          <Plus size={12} weight="bold" /> {t("qms.new")}
        </button>
      </div>

      {okMsg && (
        <div className="inline-flex items-center gap-1 text-sm text-emerald-700" data-testid={`qms-${type}-ok`}>
          <CheckCircle size={14} weight="fill" /> {okMsg}
        </div>
      )}

      {/* Editor */}
      {editing !== null && (
        <div className="grid-card p-4 space-y-4 border-l-4 border-l-blue-600" data-testid={`qms-${type}-editor`}>
          <div className="flex items-center justify-between">
            <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">
              {editing === "new" ? t("qms.new") : `${t("qms.edit")} · ${draft.ref || ""}`}
            </div>
            <button onClick={() => setEditing(null)} data-testid={`qms-${type}-editor-close`} className="p-1 hover:bg-zinc-100 rounded-sm"><X size={14} /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <Field label={t("qms.rec_title")} value={draft.title} onChange={(v) => setDraft({ ...draft, title: v })} testid={`qms-${type}-f-title`} />
            </div>
            <Select label={t("qms.status")} value={draft.status} onChange={(v) => setDraft({ ...draft, status: v })}
              options={STATUSES.map((s) => [s, t(`qms.st.${s}`)])} testid={`qms-${type}-f-status`} />
            <Select label={t("qms.severity")} value={draft.severity} onChange={(v) => setDraft({ ...draft, severity: v })}
              options={[["", "—"], ...SEVERITIES.map((s) => [s, t(`qms.sev.${s}`)])]} testid={`qms-${type}-f-severity`} />
            <Field label={t("qms.date_opened")} type="date" value={draft.date_opened} onChange={(v) => setDraft({ ...draft, date_opened: v })} testid={`qms-${type}-f-opened`} />
            <Field label={t("qms.date_closed")} type="date" value={draft.date_closed} onChange={(v) => setDraft({ ...draft, date_closed: v })} testid={`qms-${type}-f-closed`} />
            <div className="md:col-span-3">
              <Field label={t("qms.responsible")} value={draft.responsible} onChange={(v) => setDraft({ ...draft, responsible: v })} testid={`qms-${type}-f-responsible`} />
            </div>
          </div>
          <Area label={t("qms.description")} value={draft.description} onChange={(v) => setDraft({ ...draft, description: v })} testid={`qms-${type}-f-description`} />

          {type === "capa" && (
            <Select
              label={t("qms.link_origin")}
              value={draft.source_qms_record_id || ""}
              onChange={(sourceId) => {
                const source = originRecords.find((item) => item.id === sourceId);
                setDraft({
                  ...draft,
                  source_qms_record_id: sourceId,
                  trace_record_id: source?.trace_record_id || draft.trace_record_id || "",
                  details: {
                    ...draft.details,
                    source_qms_ref: source?.ref || "",
                    ...(source?.batch_lot ? { device_lot: source.batch_lot } : {}),
                  },
                });
              }}
              options={[
                ["", t("qms.no_origin")],
                ...originRecords.map((item) => [item.id, `${item.ref} · ${item.title}`]),
              ]}
              testid="qms-capa-f-origin"
            />
          )}

          {["complaint", "ncr", "recall", "fsca", "capa"].includes(type) && (
            <Select
              label={t("trace.link_lot")}
              value={draft.trace_record_id || ""}
              onChange={(traceId) => {
                const linked = traceRecords.find((item) => item.id === traceId);
                setDraft({
                  ...draft,
                  trace_record_id: traceId,
                  details: {
                    ...draft.details,
                    ...(linked ? { device_lot: linked.batch_lot || "" } : {}),
                  },
                });
              }}
              options={[
                ["", t("trace.no_linked_lot")],
                ...traceRecords.map((item) => [
                  item.id,
                  `${item.product}${item.variant_name ? ` · ${item.variant_name}` : ""}${item.batch_lot ? ` · ${t("trace.batch")}: ${item.batch_lot}` : ""}${item.ref ? ` · ${item.ref}` : ""}`,
                ]),
              ]}
              testid="qms-complaint-f-trace-record"
            />
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {fields.map((f) => {
              const val = draft.details[f.k] || "";
              const set = (v) => setDraft({ ...draft, details: { ...draft.details, [f.k]: v } });
              const label = t(`qms.f.${f.k}`);
              const testid = `qms-${type}-f-${f.k}`;
              if (f.type === "area") return <div key={f.k} className="md:col-span-2"><Area label={label} value={val} onChange={set} testid={testid} /></div>;
              if (f.type === "select") return <Select key={f.k} label={label} value={val} onChange={set} options={[["", "—"], ...f.options.map((o) => [o, f.optionPrefix ? t(`${f.optionPrefix}${o}`) : o])]} testid={testid} />;
              return <Field key={f.k} label={label} type={f.type} value={val} onChange={set} testid={testid} />;
            })}
          </div>

          <div className="flex items-center gap-3 pt-2 border-t border-zinc-100">
            <button onClick={save} disabled={saving || !draft.title.trim()} data-testid={`qms-${type}-save`}
              className="btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm disabled:opacity-40">
              <FloppyDisk size={14} weight="bold" /> {saving ? "…" : t("qms.save")}
            </button>
            <button onClick={() => setEditing(null)} data-testid={`qms-${type}-cancel`}
              className="border border-zinc-200 hover:bg-zinc-50 px-4 py-2 rounded-sm text-sm font-semibold transition-colors">
              {t("qms.cancel")}
            </button>
            {type === "complaint" && editing !== "new" && draft.details.reportable === "Yes" && (
              <button onClick={createFscaDraft} disabled={creatingFsca} data-testid="qms-complaint-make-fsca"
                className="inline-flex items-center gap-2 border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 px-4 py-2 rounded-sm text-sm font-semibold transition-colors disabled:opacity-40">
                {creatingFsca ? "…" : t("qms.make_fsca")}
              </button>
            )}
          </div>
        </div>
      )}

      {/* List */}
      <div className="grid-card p-0 overflow-x-auto">
        {filtered.length === 0 ? (
          <div className="text-xs text-zinc-500 font-mono py-8 text-center" data-testid={`qms-${type}-empty`}>{t("qms.no_records")}</div>
        ) : (
          <table className="w-full text-sm" data-testid={`qms-${type}-list`}>
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50 text-left">
                <Th>{t("qms.ref")}</Th>
                <Th>{t("qms.rec_title")}</Th>
                <Th>{t("qms.severity")}</Th>
                <Th>{t("qms.status")}</Th>
                <Th>{t("qms.date_opened")}</Th>
                <Th>{t("qms.responsible")}</Th>
                <Th>{t("qms.rev")}</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((rec) => (
                <tr key={rec.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors" data-testid={`qms-${type}-row-${rec.ref}`}>
                  <td className="px-3 py-2 font-mono text-xs font-bold">{rec.ref}</td>
                      <td className="px-3 py-2">{rec.title}{rec.source_qms_ref && <span className="block text-xs text-[var(--text-2)]">{t("qms.link_origin")}: {rec.source_qms_ref}</span>}{rec.batch_lot && <span className="block text-xs text-[var(--text-2)]">{t("trace.batch")}: {rec.batch_lot}{rec.variant_name ? ` · ${rec.variant_name}` : ""}</span>}</td>
                  <td className="px-3 py-2 text-xs uppercase font-mono">{rec.severity ? t(`qms.sev.${rec.severity}`) : "—"}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block border rounded-sm px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STATUS_STYLE[rec.status] || ""}`}>
                      {t(`qms.st.${rec.status}`)}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{rec.date_opened || "—"}</td>
                  <td className="px-3 py-2 text-xs">{rec.responsible || "—"}</td>
                  <td className="px-3 py-2 font-mono text-xs">r{rec.rev || 1}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => startEdit(rec)} title={t("qms.edit")} data-testid={`qms-${type}-edit-${rec.ref}`}
                        className="p-1.5 hover:bg-blue-100 text-blue-700 rounded-sm"><PencilSimple size={14} weight="bold" /></button>
                      <button onClick={() => downloadPdf(rec)} disabled={downloading === rec.id} title={t("qms.download_pdf")} data-testid={`qms-${type}-pdf-${rec.ref}`}
                        className="p-1.5 hover:bg-blue-100 text-blue-700 rounded-sm disabled:opacity-40"><FilePdf size={14} weight="bold" /></button>
                      <button onClick={() => exportJson(rec)} title={t("exports.download_json")} data-testid={`qms-${type}-json-${rec.ref}`}
                         className="rounded-sm p-1.5 text-[var(--text)] hover:bg-[var(--surface-hover)]" aria-label={`${t("exports.download_json")} ${rec.ref}`}>
                        <span className="font-mono text-[9px] font-bold">JSON</span>
                      </button>
                      <button onClick={() => remove(rec)} title={t("qms.delete")} data-testid={`qms-${type}-del-${rec.ref}`}
                        className="p-1.5 hover:bg-red-100 text-red-700 rounded-sm"><Trash size={14} weight="bold" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, accent = "text-zinc-900", testid }) {
  return (
    <div className="grid-card p-3" data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`text-2xl font-extrabold ${accent}`}>{value}</div>
    </div>
  );
}

function Th({ children }) {
  return <th className="px-3 py-2 text-[10px] font-mono uppercase tracking-wider text-zinc-500 font-semibold">{children}</th>;
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase text-zinc-600">{label}</label>
      <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-1 w-full border border-zinc-200 px-2 py-1.5 rounded-sm text-sm focus:border-blue-600 focus:outline-none" />
    </div>
  );
}

function Area({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase text-zinc-600">{label}</label>
      <textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-1 w-full border border-zinc-200 px-2 py-1.5 rounded-sm text-sm focus:border-blue-600 focus:outline-none" />
    </div>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase text-zinc-600">{label}</label>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-1 w-full border border-zinc-200 px-2 py-1.5 rounded-sm text-sm bg-white focus:border-blue-600 focus:outline-none">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}
